import { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { useTranslation } from 'react-i18next';
import { Clock, Route } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedBadge, ThemedCard } from '../themed';
import { formatDateAwareTime } from '../../utils/dateTimeFormatter';

const ActiveDriversPanel = ({
  entityId,
  refreshKey = 0,
  preferredDateFormat = null,
  preferredTimeFormat = '12h'
}) => {
  const { t } = useTranslation();
  const { background, border, text, cn } = useTheme();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchActiveDrivers = async () => {
      if (!entityId) {
        setDrivers([]);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/driver-availability/${entityId}/active`);
        if (!cancelled) {
          setDrivers(Array.isArray(response.data?.data) ? response.data.data : []);
        }
      } catch (error) {
        if (!cancelled) {
          setDrivers([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    fetchActiveDrivers();

    return () => {
      cancelled = true;
    };
  }, [entityId, refreshKey]);

  return (
    <ThemedCard className="mb-6">
      <ThemedCard.Header>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={cn('text-base font-semibold', text.primary)}>
              {t('driverAvailability.admin.activeDrivers')}
            </span>
            <ThemedBadge variant="success">{drivers.length}</ThemedBadge>
          </div>
        </div>
      </ThemedCard.Header>
      <ThemedCard.Body>
        {loading ? (
          <div className={cn('text-sm', text.secondary)}>...</div>
        ) : drivers.length === 0 ? (
          <div className={cn('text-sm', text.secondary)}>
            {t('driverAvailability.admin.noActiveDrivers')}
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {drivers.map((driver) => (
              <div
                key={driver.id}
                className={cn(
                  'rounded-lg border p-4 flex flex-col gap-2',
                  background.secondary,
                  border.default
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className={cn('font-medium', text.primary)}>
                    {driver.driver_name || `#${driver.driver_id}`}
                  </div>
                  <ThemedBadge variant="success">{t('driverAvailability.onDuty')}</ThemedBadge>
                </div>

                <div className={cn('text-sm flex items-center gap-2', text.secondary)}>
                  <Clock className="h-4 w-4" />
                  <span>
                    {t('driverAvailability.onDutySince', {
                      time: formatDateAwareTime(driver.started_at, preferredDateFormat, preferredTimeFormat)
                    })}
                  </span>
                </div>

                <div className={cn('text-sm flex items-center gap-2', text.secondary)}>
                  <Clock className="h-4 w-4" />
                  <span>
                    {t('driverAvailability.expectedUntil', {
                      time: formatDateAwareTime(driver.expected_end_at, preferredDateFormat, preferredTimeFormat)
                    })}
                  </span>
                </div>

                {driver.route_name && (
                  <div className={cn('text-sm flex items-center gap-2', text.secondary)}>
                    <Route className="h-4 w-4" />
                    <span>
                      {t('driverAvailability.preferredRouteLabel')}: {driver.route_name}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ThemedCard.Body>
    </ThemedCard>
  );
};

export default ActiveDriversPanel;
